import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Select from 'react-select';
import { FaFilter, FaTimes } from 'react-icons/fa';
import { CSVLink } from 'react-csv';
import { useSelector } from 'react-redux';

const StatusSummaryChart = () => {
  const pid = useSelector(state => state.user.projectId);
  
  // Chart data state
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [orgOptions, setOrgOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [targetGroupOptions, setTargetGroupOptions] = useState([]);
  
  // Selected filters state
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedTargetGroup, setSelectedTargetGroup] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [weeksPerPage, setWeeksPerPage] = useState(30);

  // State for info popups
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  // Definitions for info popups
  const definitions = {
    "Week wise Applications reaching Benefit Received status": {
      title: "Week wise Applications reaching Benefit Received status",
      description: "This chart shows the weekly trend of applications that have reached the 'Benefit Received' status. Each bar represents the total number of applications that successfully received benefits during that week.",
      example: "• Week 1: 150 applications received benefits\n• Week 2: 200 applications received benefits\n• Week 3: 180 applications received benefits"
    },
    "Organization": {
      title: "Organization",
      description: "Filter the data by specific organization/workspace to analyze weekly BR trends for that organization.",
      example: "Example: Maharashtra, Karnataka, Delhi NCR, etc."
    },
    "Service District": {
      title: "Service District",
      description: "Filter the data by service district to analyze weekly BR trends for specific geographic regions.",
      example: "Example: Mumbai, Bangalore, Delhi, etc."
    },
    "Service Name": {
      title: "Service Name",
      description: "Filter the data by specific service/scheme name to analyze weekly BR trends for that service.",
      example: "Example: PM Awas Yojana, Ayushman Bharat, etc."
    },
    "Service Category": {
      title: "Service Category",
      description: "Filter the data by service category to analyze weekly BR trends for specific service types.",
      example: "Example: Schemes, Document, DFL, Yojana Card"
    },
    "Week Number": {
      title: "Week Number",
      description: "The sequential week number in the data. Week 1 represents the first week in the dataset.",
      example: "Week 1, Week 2, Week 3, etc."
    },
    "Week Start Date": {
      title: "Week Start Date",
      description: "The starting date of the week for which data is shown.",
      example: "Example: 01-Jan-2026"
    },
    "Week End Date": {
      title: "Week End Date",
      description: "The ending date of the week for which data is shown.",
      example: "Example: 07-Jan-2026"
    },
    "Applications with Benefit Received": {
      title: "Applications with Benefit Received",
      description: "The total number of applications that reached 'Benefit Received' status during that week. These are applications where citizens have successfully received benefits.",
      example: "Example: 150 applications received benefits this week"
    }
  };

  // Info Button Component
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  // Info Popup Component
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]" onClick={closeInfoPopup}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn" onClick={(e) => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close info popup
  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  // Fetch chart data
  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);
        
        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/week-wise/?${params}`);
        const data = await response.json();
        setChartData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchChartData();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Fetch organization options
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);

        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/unique-orgs/?${params}`);
        const data = await response.json();
        setOrgOptions((data.orgs || []).map(org => ({ value: org, label: org })));
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setOrgOptions([]);
      }
    };
    fetchOrgs();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Fetch district options
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);

        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/unique-districts/?${params}`);
        const data = await response.json();
        setDistrictOptions((data.districts || []).map(d => ({ value: d, label: d })));
      } catch (error) {
        console.error('Error fetching districts:', error);
        setDistrictOptions([]);
      }
    };
    fetchDistricts();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Fetch scheme options
  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);

        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/unique-schemes/?${params}`);
        const data = await response.json();
        setSchemeOptions((data.schemes || []).map(s => ({ value: s, label: s })));
      } catch (error) {
        console.error('Error fetching schemes:', error);
        setSchemeOptions([]);
      }
    };
    fetchSchemes();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Fetch target group options
  useEffect(() => {
    const fetchTargetGroups = async () => {
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);

        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/unique-tg/?${params}`);
        const data = await response.json();
        setTargetGroupOptions((data.tg || []).map(g => ({ value: g, label: g })));
      } catch (error) {
        console.error('Error fetching target groups:', error);
        setTargetGroupOptions([]);
      }
    };
    fetchTargetGroups();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedOrg(null);
    setSelectedDistrict(null);
    setSelectedScheme(null);
    setSelectedTargetGroup(null);
    setCurrentPage(0);
  };

  // Pagination calculations
  const totalPages = Math.ceil(chartData.length / weeksPerPage);
  const paginatedData = chartData.slice(
    currentPage * weeksPerPage,
    (currentPage + 1) * weeksPerPage
  ).map(item => ({
    week: `Week ${item.week_number} (${item.week_start_date})`,
    weekNumber: item.week_number,
    weekStartDate: item.week_start_date,
    weekEndDate: item.week_end_date,
    brValue: item.total_br_value,
    startDate: item.week_start_date,
    endDate: item.week_end_date
  }));

  // Prepare CSV data
  const prepareCSV = () => {
    const headers = ['Week Number', 'Week Start Date', 'Week End Date', 'Applications with Benefit Received'];
    const rows = chartData.map(item => [
      item.week_number,
      item.week_start_date,
      item.week_end_date,
      item.total_br_value || 0
    ]);
    return [headers, ...rows];
  };

  const getCSVFilename = () => {
    const date = new Date().toISOString().split('T')[0];
    return `week-wise-br-${date}.csv`;
  };

  const dynamicBarSize = Math.max(8, Math.min(60, Math.floor(1000 / (paginatedData.length || 1))));

  return (
    <div className="bg-white rounded-xl shadow-sm shadow-black border px-2 py-1 ml-[4vw] max-w-[90vw] mb-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h2 className="text-xl font-bold text-pink-800">Week wise Applications reaching Benefit Received status</h2>
          <InfoButton title="Week wise Applications reaching Benefit Received status" />
        </div>
        <div className="flex items-center space-x-2">
          <FaFilter className="text-gray-500" />
          <span className="text-sm text-gray-600">Filters</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-1">
        <div className="flex items-center gap-1">
          <Select
            className="text-sm flex-1"
            isClearable
            placeholder="Select Org/workspace"
            value={selectedOrg}
            onChange={setSelectedOrg}
            options={orgOptions}
          />
          <InfoButton title="Organization" />
        </div>
        <div className="flex items-center gap-1">
          <Select
            className="text-sm flex-1"
            isClearable
            placeholder="Select Service District"
            value={selectedDistrict}
            onChange={setSelectedDistrict}
            options={districtOptions}
          />
          <InfoButton title="Service District" />
        </div>
        <div className="flex items-center gap-1">
          <Select
            className="text-sm flex-1"
            isClearable
            placeholder="Select ServiceName"
            value={selectedScheme}
            onChange={setSelectedScheme}
            options={schemeOptions}
          />
          <InfoButton title="Service Name" />
        </div>
        <div className="flex items-center gap-1">
          <Select
            className="text-sm flex-1"
            isClearable
            placeholder="Select Service Category"
            value={selectedTargetGroup}
            onChange={setSelectedTargetGroup}
            options={targetGroupOptions}
          />
          <InfoButton title="Service Category" />
        </div>
        <button
          className="p-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 flex items-center justify-center space-x-1 transition-colors"
          onClick={clearFilters}
        >
          <FaTimes />
          <span>Clear Filters</span>
        </button>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-2">
        <div className="flex items-center space-x-2">
          <label htmlFor="weeks-per-page" className="text-sm text-gray-600">Weeks per page:</label>
          <select
            id="weeks-per-page"
            value={weeksPerPage}
            onChange={(e) => {
              setWeeksPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            className="border border-gray-300 px-4 py-1 rounded text-sm"
          >
            {[10, 20, 30, 50, 100, 200, 400].map(value => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>
        <div>
          <span className="text-sm font-bold text-purple-900">
            Showing weeks {currentPage * weeksPerPage + 1}-
            {Math.min((currentPage + 1) * weeksPerPage, chartData.length)} of {chartData.length}
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
            className={`px-4 py-1 rounded-md text-sm ${
              currentPage === 0 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-pink-600 text-white hover:bg-pink-700'
            }`}
          >
            Previous
          </button>
          <span className="text-sm font-bold text-purple-900">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={(currentPage + 1) * weeksPerPage >= chartData.length}
            className={`px-4 py-1 rounded-md text-sm ${
              (currentPage + 1) * weeksPerPage >= chartData.length
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-pink-600 text-white hover:bg-pink-700'
            }`}
          >
            Next
          </button>
          <CSVLink
            data={prepareCSV()}
            filename={getCSVFilename()}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded-md text-sm transition flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </CSVLink>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
        </div>
      ) : chartData.length > 0 ? (
        <div className="w-full overflow-x-auto">
          <div className="min-w-[800px]">
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={paginatedData} margin={{ top: 40, right: 30, left: 20, bottom: 60 }}>
                <defs>
                  <linearGradient id="brGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.9} />
                    <stop offset="50%" stopColor="#6366F1" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.9} />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="week"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12 }}
                  label={{
                    value: 'Week Number',
                    position: 'insideBottom',
                    offset: -50,
                    fontSize: 14,
                    fontWeight: 'bold',
                  }}
                />
                <YAxis 
                  label={{
                    value: 'BR Count',
                    angle: -90,
                    position: 'insideLeft',
                    fontSize: 14,
                    fontWeight: 'bold',
                  }}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload?.length) {
                      const { weekStartDate, weekEndDate, brValue, weekNumber } = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
                          <p className="font-bold text-gray-800">Week {weekNumber}</p>
                          <p className="text-sm text-gray-600">{weekStartDate} to {weekEndDate}</p>
                          <p className="mt-1 text-blue-600 font-semibold">Applications with Benefit Received: {brValue}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend verticalAlign="top" align="center" />
                <Bar
                  dataKey="brValue"
                  name="Applications with Benefit Received"
                  fill="url(#brGradient)"
                  radius={[6, 6, 0, 0]}
                  barSize={dynamicBarSize}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500 text-lg">No data available for the selected filters</p>
        </div>
      )}

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default StatusSummaryChart;